import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Button,
  Stack,
} from "@mui/material";
import {
  FolderShared as ResourceIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import api from "../../api";

const subjectColor = (key) =>
  ({
    maths: "#1565c0",
    english: "#6a1b9a",
    "verbal-reasoning": "#2e7d32",
    "non-verbal-reasoning": "#ef6c00",
  }[key] || "#455a64");

/** Approved tutor content for the student's group. Pending uploads never appear here. */
export default function GroupResources() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("student/group-resources");
      // Files only here; the week's worksheets are listed on Homework and the Work Sheet page.
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(list.filter((r) => r.kind !== "worksheet"));
      setMessage(res.data?.message || "");
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load your resources.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ResourceIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Class Resources
          </Typography>
        </Stack>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Notes and materials shared by your tutor.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          {[1, 2].map((n) => (
            <Skeleton key={n} variant="rounded" height={110} />
          ))}
        </Stack>
      ) : rows.length === 0 && !error ? (
        <Alert severity="info">{message || "Your tutor has not shared any resources yet."}</Alert>
      ) : (
        <Stack spacing={2}>
          {rows.map((r) => (
            <Card key={r.id} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "center" }}
                  spacing={2}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                      {r.subject?.name && (
                        <Chip
                          size="small"
                          label={r.subject.name}
                          sx={{
                            bgcolor: `${subjectColor(r.subject.key)}18`,
                            color: subjectColor(r.subject.key),
                            fontWeight: 700,
                          }}
                        />
                      )}
                      {/* Where the tutor filed it in the syllabus, so students can tell two
                          uploads on the same subject apart. */}
                      {r.topic?.name && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={r.subtopic?.name ? `${r.topic.name} › ${r.subtopic.name}` : r.topic.name}
                        />
                      )}
                    </Stack>
                    <Typography variant="h6" fontWeight={700}>
                      {r.title}
                    </Typography>
                    {r.description && (
                      <Typography variant="body2" color="text.secondary">
                        {r.description}
                      </Typography>
                    )}
                    {r.tutor && (
                      <Typography variant="caption" color="text.secondary">
                        Shared by {r.tutor}
                      </Typography>
                    )}
                  </Box>
                  {r.file_url && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      component="a"
                      href={r.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ flexShrink: 0 }}
                    >
                      Open
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
